// Geographic helpers for the pathfinder. All positions are {lat, lng}.

const EARTH_R = 6371000; // meters

/** Great-circle distance in meters between two {lat, lng} points. */
export function haversineMeters(a, b) {
  const toRad = d => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_R * Math.asin(Math.sqrt(s));
}

/** Fit a panorama's heading (bearing its centre faces) + fov (degrees across)
 *  from (bearing, frac) correspondences — the model bearing = heading +
 *  (frac − 0.5)·fov. Returns null with no usable pairs; with one pair uses
 *  `defaultFov`; with several, least-squares.
 *
 *  Bearings are unwrapped by walking the pairs **in fraction order**, each one
 *  taken as the previous plus a forward (clockwise) turn. Two things make that
 *  the right rule rather than the obvious ones:
 *   · Unwrapping around the first pair cannot fit a full 360° photo at all —
 *     its fractions wrap through 0, so the corridor behind you lands ±180° from
 *     wherever the list happened to start and the line is fitted to a fold.
 *   · Taking the *shortest* turn breaks on the commonest shape in a building: a
 *     straight corridor puts two paths exactly 180° apart, and a 180° turn has
 *     no shorter direction, so it resolves backwards and drags the fit with it.
 *  Bearing rises with fraction across the image, so forward is always correct. */
export function fitPanoHeadingFov(pairs, { defaultFov = 180 } = {}) {
  const n360 = d => ((d % 360) + 360) % 360;
  const pts = (pairs || []).filter(p => typeof p.frac === 'number' && typeof p.bearing === 'number')
    .slice().sort((a, b) => a.frac - b.frac);
  if (!pts.length) return null;
  if (pts.length === 1) return { heading: n360(pts[0].bearing - (pts[0].frac - 0.5) * defaultFov), fov: defaultFov };
  let mx = 0, my = 0;
  const xs = pts.map(p => p.frac - 0.5);
  const ys = [pts[0].bearing];
  for (let i = 1; i < pts.length; i++) ys.push(ys[i - 1] + n360(pts[i].bearing - ys[i - 1]));
  for (let i = 0; i < pts.length; i++) { mx += xs[i]; my += ys[i]; }
  mx /= pts.length; my /= pts.length;
  let num = 0, den = 0;
  for (let i = 0; i < pts.length; i++) { num += (xs[i] - mx) * (ys[i] - my); den += (xs[i] - mx) ** 2; }
  const fov = den > 1e-9 ? num / den : defaultFov;
  return { heading: n360(my - fov * mx), fov: Math.abs(fov) < 1e-6 ? defaultFov : fov };
}

/** A point `meters` from `origin` along compass `bearing` (deg, 0 = N). */
export function destinationPoint(origin, bearing, meters) {
  const toR = d => (d * Math.PI) / 180, toD = r => (r * 180) / Math.PI;
  const br = toR(bearing), lat1 = toR(origin.lat), lng1 = toR(origin.lng), dr = meters / EARTH_R;
  const lat2 = Math.asin(Math.sin(lat1) * Math.cos(dr) + Math.cos(lat1) * Math.sin(dr) * Math.cos(br));
  const lng2 = lng1 + Math.atan2(Math.sin(br) * Math.sin(dr) * Math.cos(lat1), Math.cos(dr) - Math.sin(lat1) * Math.sin(lat2));
  return { lat: toD(lat2), lng: toD(lng2) };
}

/** Compass bearing in degrees (0 = North, clockwise) from a → b. */
export function bearingDeg(a, b) {
  const toR = d => (d * Math.PI) / 180, toD = r => (r * 180) / Math.PI;
  const dLng = toR(b.lng - a.lng);
  const y = Math.sin(dLng) * Math.cos(toR(b.lat));
  const x = Math.cos(toR(a.lat)) * Math.sin(toR(b.lat)) - Math.sin(toR(a.lat)) * Math.cos(toR(b.lat)) * Math.cos(dLng);
  return (toD(Math.atan2(y, x)) + 360) % 360;
}

/**
 * Ray-cast point-in-polygon. `ring` is an array of {lat, lng}; an explicitly
 * closed ring (last point repeating the first) is fine.
 */
export function pointInPolygon(pt, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const yi = ring[i].lat, xi = ring[i].lng;
    const yj = ring[j].lat, xj = ring[j].lng;
    const intersects =
      (yi > pt.lat) !== (yj > pt.lat) &&
      pt.lng < ((xj - xi) * (pt.lat - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

/**
 * Nearest node to `pt` among `nodes`, optionally filtered and capped at
 * `maxMeters`. Returns {node, meters} or null.
 */
export function nearestNode(nodes, pt, { maxMeters = Infinity, filter } = {}) {
  let best = null, bestDist = maxMeters;
  for (const node of nodes) {
    if (filter && !filter(node)) continue;
    const d = haversineMeters(pt, node);
    if (d <= bestDist) { bestDist = d; best = node; }
  }
  return best ? { node: best, meters: bestDist } : null;
}
